import type {Component, EditorConfig} from 'grapesjs';
import formMakerPlugin from '@FormMakerReact/grapesjs-form-maker-plugin/index';

const BRAND_OUTLINE_COLOR = '#3FBCE7';

function getFormMakerFrameStyle(): string {
  return `
        body.gjs-dashed {
            align-items: center;
            background-color: transparent;
            display: flex;
            justify-content: center;
            margin: 0 2px;
        }
        .pmw-canvas-hidden {
            display: none !important;
        }
        .pmw-canvas-visible {
            display: block !important;
        }
    `;
}

// canvasCss is injected after GrapesJS' built-in state rules, so source order wins the cascade.
function getFormMakerCanvasCss(): string {
  return `
        .gjs-dashed *[data-gjs-highlightable],
        [data-gjs-highlightable="true"] {
            outline: none !important;
        }
        .hovered, .gjs-hovered {
            outline: 2px dashed ${BRAND_OUTLINE_COLOR} !important;
        }
        .selected, .gjs-selected {
            outline: 2px solid ${BRAND_OUTLINE_COLOR} !important;
            outline-offset: 0;
        }
    `;
}

function buildBadgeLabel(component: Component): string {
  const iconValue: unknown = component.get('icon');
  const nameValue: unknown = component.get('name');
  const icon = typeof iconValue === 'string' ? iconValue : '';
  const name = typeof nameValue === 'string' ? nameValue : '';
  const iconHtml = icon ? `<i class="${icon} toolbar-icon size-icon-16" style="display:inline-flex;align-items:center;"></i>` : '';
  return `<span style="display:inline-flex;align-items:center;gap:6px;">${iconHtml}<span class="body-md-regular toolbar-name">${name}</span></span>`;
}

// Override cssIcons / blockManager to nullable so we can disable GrapesJS' built-in defaults
// (we render the block manager in React and ship our own icon CSS). `mode` is a legacy
// GrapesJS editor option carried over from the CI3/legacy form-maker config and is not in
// the 0.22.x EditorConfig type.
type FormMakerEditorConfig = Omit<EditorConfig, 'cssIcons' | 'blockManager'> & {
  cssIcons?: EditorConfig['cssIcons'] | null;
  blockManager?: EditorConfig['blockManager'] | null;
  mode?: string;
};

const GRAPESJS_CONFIG: FormMakerEditorConfig = {
  mode: 'simple',
  forceClass: false,
  avoidInlineStyle: false,
  height: '100%',
  multipleSelection: false,
  cssIcons: null,
  blockManager: null,
  // Drop GrapesJS' default body background and outlines so editor-canvas.css can paint freely.
  protectedCss: '* { box-sizing: border-box; } body { margin: 0; }',
  // The React hook's useBeforeUnloadGuard owns the "unsaved changes" prompt; GrapesJS's copy would also prompt mid-save.
  noticeOnUnload: false,
  canvasCss: getFormMakerCanvasCss(),
  canvas: {
    frameContent: '',
    frameStyle: getFormMakerFrameStyle(),
    styles: [window.PMW.util.asset_url('css/components/form-maker-react/editor-canvas.css?v=1.0')],
    customBadgeLabel: buildBadgeLabel,
  },
  keymaps: {
    defaults: {},
  },
  richTextEditor: {
    stylePrefix: 'rte-',
    adjustToolbar: true,
    actions: ['bold', 'italic', 'underline', 'strikethrough', 'link'],
  },
  storageManager: {
    type: 'form-maker-storage',
    autosave: false,
    stepsBeforeSave: 1,
  },
  panels: {
    defaults: [],
  },
  selectorManager: {
    componentFirst: true,
  },
  deviceManager: {
    default: 'desktop',
    devices: [
      {id: 'desktop', name: 'Desktop', width: '100%', widthMedia: '767px'},
      {id: 'tablet', name: 'Tablet', width: '600px', widthMedia: '768px'},
      {id: 'mobile', name: 'Mobile', width: '345px', widthMedia: '500px'},
    ],
  },
  undoManager: {
    trackSelection: false,
  },
  parser: {
    optionsHtml: {
      allowUnsafeAttr: false,
      allowUnsafeAttrValue: false,
      allowScripts: false,
    },
  },
  plugins: [formMakerPlugin],
};

export {GRAPESJS_CONFIG as default};
